import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, LayoutGrid, List, DollarSign } from 'lucide-react';
import { apiRequest } from '../../config/api';
import { useToast } from '../../context/ToastContext';
import usePipelines from '../../hooks/usePipelines';
import PipelineBoard from '../../components/crm/PipelineBoard';
import PipelineListView from '../../components/crm/PipelineListView';

export default function FastestFormCRMPipelineView() {
  const { pipelineId } = useParams();
  const navigate = useNavigate();
  const { showError } = useToast();
  const { moveDeal } = usePipelines();

  const [pipeline, setPipeline] = useState(null);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('kanban');

  const fetchPipeline = useCallback(async () => {
    try {
      const response = await apiRequest(`/crm/pipelines/${pipelineId}`);
      if (response.ok) {
        const data = await response.json();
        setPipeline(data);
      } else {
        showError('Pipeline not found');
        navigate('/data/pipelines');
      }
    } catch (error) {
      navigate('/data/pipelines');
    }
  }, [pipelineId, showError, navigate]);

  const fetchDeals = useCallback(async () => {
    try {
      const response = await apiRequest(`/crm/deals/?pipeline_id=${pipelineId}`);
      if (response.ok) {
        const data = await response.json();
        setDeals(data);
      }
    } catch (error) {
      showError('Failed to load deals');
    }
  }, [pipelineId, showError]);

  useEffect(() => {
    Promise.all([fetchPipeline(), fetchDeals()]).then(() => setLoading(false));
  }, [fetchPipeline, fetchDeals]);

  const handleMoveDeal = async (dealId, stageId) => {
    // Optimistic update
    setDeals((prev) =>
      prev.map((d) => {
        if (d.id === dealId) {
          const newStage = pipeline?.stages?.find((s) => s.id === stageId);
          return {
            ...d,
            stage_id: stageId,
            stage_name: newStage?.name || d.stage_name,
            stage_color: newStage?.color || d.stage_color,
          };
        }
        return d;
      })
    );

    const result = await moveDeal(dealId, stageId);
    if (!result) {
      // Rollback on failure
      fetchDeals();
    }
  };

  const handleDealClick = (deal) => {
    navigate(`/crm/companies/${deal.company_id}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  if (!pipeline) return null;

  const totalDeals = deals.length;
  const totalValue = deals.reduce((sum, d) => sum + (d.value || 0), 0);
  const formattedTotal = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(totalValue);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/data/pipelines')}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {pipeline.name}
            </h2>
            {pipeline.description && (
              <p className="text-sm text-gray-500 dark:text-gray-400">{pipeline.description}</p>
            )}
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
          <button
            onClick={() => setViewMode('kanban')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'kanban'
                ? 'bg-white dark:bg-gray-700 text-brand-600 dark:text-brand-400 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
            }`}
          >
            <LayoutGrid className="w-4 h-4" />
            Board
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'list'
                ? 'bg-white dark:bg-gray-700 text-brand-600 dark:text-brand-400 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
            }`}
          >
            <List className="w-4 h-4" />
            List
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="flex items-center gap-6 mb-6 text-sm">
        <div className="text-gray-500 dark:text-gray-400">
          <span className="font-medium text-gray-900 dark:text-gray-100">{totalDeals}</span> deals
        </div>
        <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
          <DollarSign className="w-4 h-4" />
          <span className="font-medium text-gray-900 dark:text-gray-100">{formattedTotal}</span> total value
        </div>
      </div>

      {/* View Content */}
      {viewMode === 'kanban' ? (
        <PipelineBoard
          stages={pipeline.stages || []}
          deals={deals}
          onMoveDeal={handleMoveDeal}
          onDealClick={handleDealClick}
        />
      ) : (
        <PipelineListView
          deals={deals}
          stages={pipeline.stages || []}
          onDealClick={handleDealClick}
        />
      )}
    </div>
  );
}
